import type { StaffRequestDetail as Detail, Me } from "@hoffman-support/contracts";
import { type FormEvent, useEffect, useState } from "react";
import { ApiError } from "../api";
import { staffGet, staffPatch, staffReply } from "../staff-api";
import { Attachments } from "./Attachments";
import { FreeScoutMappings } from "./FreeScoutMappings";
import { STATUS_LABELS } from "./RequestList";

/** The internal/public distinction carries a label and a border, never colour alone (§11.7). */
const INTERNAL_STYLE = {
  borderLeft: "4px solid #7a4b00",
  background: "#fff8ec",
  padding: "0.5rem 0.75rem",
  marginBottom: "0.75rem",
};
const PUBLIC_STYLE = {
  borderLeft: "4px solid #2a7",
  padding: "0.5rem 0.75rem",
  marginBottom: "0.75rem",
};

export function StaffRequestDetail({ me, id }: { me: Me; id: string }) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing" | "error">("loading");
  const [visibility, setVisibility] = useState<"requester" | "internal">("internal");
  const [body, setBody] = useState("");
  const [replyKey, setReplyKey] = useState(() => crypto.randomUUID());
  const [notice, setNotice] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("freescout") === "linked")
      return "FreeScout conversation linked to this request.";
    if (!params.has("created")) return "";
    if (params.get("source") === "email")
      return "Request created for the verified portal account and linked to its FreeScout conversation.";
    return params.has("bound")
      ? "Request raised. The caller already has a portal identity, so it is theirs now."
      : "Request raised. It binds to the caller the first time they sign in.";
  });
  const canWrite = me.user.roles.includes("staff") || me.user.roles.includes("admin");

  async function load() {
    try {
      setDetail(await staffGet(id));
      setState("ready");
    } catch (error) {
      setState(error instanceof ApiError && error.failure.status === 404 ? "missing" : "error");
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: load is defined per mount and reads only stable props
  useEffect(() => {
    void load();
  }, [id]);

  async function send(event: FormEvent) {
    event.preventDefault();
    if (!body.trim() || !detail) return;
    setNotice("Sending…");
    try {
      const result = await staffReply(me, replyKey, detail.id, { body, visibility });
      setBody("");
      setReplyKey(crypto.randomUUID());
      setNotice(
        result.visibility === "internal"
          ? "Internal note saved. The requester cannot see it."
          : result.redacted
            ? "Reply sent; a credential-shaped value was removed."
            : "Reply sent to the requester.",
      );
      await load();
    } catch (error) {
      setNotice(
        error instanceof ApiError ? error.failure.message : "The message could not be sent.",
      );
    }
  }

  async function patch(change: {
    status?: "in_progress" | "waiting_on_user";
    priority?: number | null;
    assignedToUserId?: string | null;
  }) {
    if (!detail) return;
    try {
      await staffPatch(me, detail.id, { version: detail.version, ...change });
      setNotice("Updated. Board fields show as pending until the applier confirms them.");
      await load();
    } catch (error) {
      setNotice(error instanceof ApiError ? error.failure.message : "The update failed.");
    }
  }

  if (state === "loading") return <p role="status">Loading…</p>;
  if (state === "missing") return <p role="alert">No request with that reference.</p>;
  if (state === "error" || !detail) return <p role="alert">The request could not be loaded.</p>;

  return (
    <article aria-labelledby="staff-request-heading">
      <p>
        <a href="/staff">Back to the queue</a>
      </p>
      <h2 id="staff-request-heading">
        {detail.publicId}: {detail.subject}
      </h2>
      <dl style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "0.25rem 1rem" }}>
        <dt>Status</dt>
        <dd>
          {STATUS_LABELS[detail.status]} {detail.boardState ? `(board: ${detail.boardState})` : ""}
          {!detail.onBoard && detail.status !== "withdrawn" ? " · not yet on the board" : ""}
        </dd>
        <dt>Requester</dt>
        <dd>
          {detail.requester.displayName ?? detail.requester.upn ?? "unknown"}
          {detail.provisional
            ? ` · raised by phone, ${detail.provisional.expired ? "binding expired" : `binds at first sign-in until ${new Date(detail.provisional.expiresAt).toLocaleDateString()}`} (${detail.provisional.verification ?? "verification not recorded"})`
            : ""}
          {detail.raisedBy ? ` · raised by ${detail.raisedBy.displayName ?? "staff"}` : ""}
        </dd>
        <dt>Category</dt>
        <dd>{detail.category}</dd>
        <dt>Priority</dt>
        <dd>
          {detail.priority ?? "unset"}
          {detail.pendingBoardPatch ? " (pending on the board)" : ""}
        </dd>
        <dt>Assigned</dt>
        <dd>{detail.assignedToDisplay ?? "unassigned"}</dd>
        <dt>Cards</dt>
        <dd>
          {detail.links.length === 0
            ? "none yet"
            : detail.links
                .map(
                  (l) => `${l.linkType} #${l.workItemId}${l.stateName ? ` (${l.stateName})` : ""}`,
                )
                .join(", ")}
        </dd>
        <dt>Triage</dt>
        <dd>
          {detail.latestRun
            ? `${detail.latestRun.outcome ?? "in progress"}${detail.latestRun.queueReason ? `, ${detail.latestRun.queueReason}` : ""}${detail.latestRun.stopReason ? `, ${detail.latestRun.stopReason}` : ""}`
            : "not yet run"}
        </dd>
        <dt>Callback</dt>
        <dd>{detail.callback}</dd>
        <dt>Location</dt>
        <dd>
          {detail.location} ({detail.onsiteOrRemote}), {detail.department}
        </dd>
        {detail.affectedSystemOrDevice ? (
          <>
            <dt>Affected</dt>
            <dd>{detail.affectedSystemOrDevice}</dd>
          </>
        ) : null}
        {detail.errorText ? (
          <>
            <dt>Error text</dt>
            <dd>
              <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{detail.errorText}</pre>
            </dd>
          </>
        ) : null}
        {Object.keys(detail.followUpAnswers).length > 0 ? (
          <>
            <dt>Details</dt>
            <dd>
              <ul style={{ margin: 0 }}>
                {Object.entries(detail.followUpAnswers).map(([k, v]) => (
                  <li key={k}>
                    {k}: {String(v)}
                  </li>
                ))}
              </ul>
            </dd>
          </>
        ) : null}
      </dl>

      {canWrite ? (
        <p>
          {detail.status === "waiting_on_user" ? (
            <button type="button" onClick={() => void patch({ status: "in_progress" })}>
              Clear waiting on requester
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void patch({ status: "waiting_on_user" })}
              disabled={["closed", "cancelled", "withdrawn"].includes(detail.status)}
            >
              Mark waiting on requester
            </button>
          )}{" "}
          <label>
            Priority{" "}
            <select
              value={detail.priority ?? ""}
              onChange={(e) =>
                void patch({ priority: e.target.value ? Number(e.target.value) : null })
              }
            >
              <option value="">unset</option>
              {[1, 2, 3, 4].map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>{" "}
          {detail.assignedToUserId === me.user.id ? (
            <button type="button" onClick={() => void patch({ assignedToUserId: null })}>
              Unassign me
            </button>
          ) : (
            <button type="button" onClick={() => void patch({ assignedToUserId: me.user.id })}>
              Assign to me
            </button>
          )}
        </p>
      ) : null}

      <Attachments me={me} requestId={detail.id} staff canUpload={canWrite} />
      <FreeScoutMappings me={me} requestId={detail.id} requesterId={detail.requester.id} />

      <section aria-labelledby="staff-thread-heading">
        <h3 id="staff-thread-heading">Conversation</h3>
        <ol style={{ listStyle: "none", padding: 0 }}>
          {detail.messages.map((m) => (
            <li
              key={m.id}
              style={m.isInternal ? INTERNAL_STYLE : PUBLIC_STYLE}
              aria-label={m.isInternal ? "Internal note" : "Visible to the requester"}
            >
              <strong>{m.isInternal ? "INTERNAL NOTE" : "TO REQUESTER"}</strong> ·{" "}
              {m.authorDisplayName ?? m.authorKind} ·{" "}
              <span style={{ color: "#555", fontSize: "0.85rem" }}>
                {new Date(m.createdAt).toLocaleString()}
              </span>
              <p style={{ whiteSpace: "pre-wrap", margin: "0.25rem 0 0" }}>{m.body}</p>
            </li>
          ))}
        </ol>
        {canWrite ? (
          <form onSubmit={send} style={visibility === "internal" ? INTERNAL_STYLE : PUBLIC_STYLE}>
            <fieldset style={{ border: 0, padding: 0, margin: "0 0 0.5rem" }}>
              <legend>
                <strong>
                  {visibility === "internal"
                    ? "Writing an INTERNAL NOTE (the requester will never see this)"
                    : "Writing TO THE REQUESTER (they will be notified)"}
                </strong>
              </legend>
              <label style={{ marginRight: "1rem" }}>
                <input
                  type="radio"
                  name="visibility"
                  value="internal"
                  checked={visibility === "internal"}
                  onChange={() => setVisibility("internal")}
                />{" "}
                Internal note
              </label>
              <label>
                <input
                  type="radio"
                  name="visibility"
                  value="requester"
                  checked={visibility === "requester"}
                  onChange={() => setVisibility("requester")}
                />{" "}
                Reply to requester
              </label>
            </fieldset>
            <label htmlFor="staff-body">Message</label>
            <br />
            <textarea
              id="staff-body"
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              style={{ width: "100%" }}
            />
            <br />
            <button type="submit">
              {visibility === "internal" ? "Save internal note" : "Send to requester"}
            </button>
          </form>
        ) : null}
      </section>
      <p role="status" aria-live="polite">
        {notice}
      </p>
    </article>
  );
}
